import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { httpGet, httpPost, httpPut } from "../../services/httpService";
import {
  getCompanyProductRulesByCompanyIdUrl,
  addCompanyProductRuleUrl,
  editCompanyProductRuleUrl,
  searchProductDrugUrl,
  getProductRuleAuditUrl,
  removeCompanyProductRuleUrl,
  getMonthlyConcessionUrl,
  getHistoricalConcessionUrl,
  addConcessionUrl,
  getTitanClaimsUrl,
  getPrescriptionImageUrl,
  getItemInfoUrl,
  saveClaimsUrl,
  processClaimUrl,
  replacementDispenseNotificationUrl,
  bulkRtecUrl,
  getClaimedCurrentUrl,
  getClaimedLastUrl,
  claimAmendmentUrl,
  getBlockedClaimsUrl,
  addEndorsementUrl, addUpdateExemptionUrl, getExemptionUrl, getDashboardStatsUrl,
  bulkExemptionUrl,
  bulkClearClaimsUrl,
  getExpiringItemsUrl,
  saveClaimPrescriptionItemsUrl,
  getClaimPrescriptionItemsUrl,
  getConcessionPricesUrl,
  getGroupModeServicesUrl,
  getFp34SummaryUrl,
  getFp34AdditionalFeesUrl,
  getPharmacyPrivateServicesIncomeUrl,
  getPharmacyPrivateServicesIncomeByDateRangeUrl,
  getDispensingIncomeUrl
} from "../../apiUrls/apiUrls";
import { logErrorWithPayload } from "../../services/logService";

// Redux Toolkit only keeps name/message/stack/code off a rejection, so a
// failing HTTP status arrives at the UI as "Request failed with status code
// 400" and the API's own explanation ("Your account is not linked to Titan")
// is lost. Re-throw that explanation as the error message so callers can show
// it. A `success: false` body is already rejected as-is and needs no help.
const withServerMessage = async (request) => {
  try {
    return await request();
  } catch (error) {
    const serverMessage =
      error?.response?.data?.message || error?.response?.data?.title;
    if (!serverMessage || error?.message === serverMessage) throw error;
    const wrapped = new Error(serverMessage);
    wrapped.cause = error;
    throw wrapped;
  }
};

const initialState = {
  isTitanTabLoading: false,
  companyProductRules: [],
  drugSearchResults: [],
  isDrugSearchLoading: false,
  productRuleAuditEntries: [],
  isAuditLoading: false,
  isRemovingProductRule: false,
  monthlyConcessions: [],
  isMonthlyConcessionLoading: false,
  historicalConcessions: [],
  isHistoricalConcessionLoading: false,
  isAddingConcession: false,
  titanClaims: [],
  isTitanClaimsLoading: false,
  claimedCurrentClaims: [],
  isClaimedCurrentClaimsLoading: false,
  claimedLastClaims: [],
  isClaimedLastClaimsLoading: false,
  blockedClaims: [],
  isBlockedClaimsLoading: false,
  groupModeServices: [],
  isServicesLoading: false,
  dashboardStats: null,
  isDashboardStatsLoading: false,
  privateServicesIncomeRange: {},
  privateServicesIncomeRangePending: 0,
  isPrivateServicesIncomeRangeLoading: false,
  dispensingIncomeRange: {},
  dispensingIncomeRangePending: 0,
  isDispensingIncomeRangeLoading: false,
};

export const getCompanyProductRulesByCompanyIdData = createAsyncThunk(
  "titanTab/getCompanyProductRulesByCompanyIdData",
  async (companyId, thunkAPI) => {
    const urlToGet = getCompanyProductRulesByCompanyIdUrl(companyId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const addCompanyProductRuleData = createAsyncThunk(
  "titanTab/addCompanyProductRuleData",
  async (payload, thunkAPI) => {
    const url = addCompanyProductRuleUrl();
    const respData = await httpPost(url, payload);
    return respData;
  },
);

export const editCompanyProductRuleData = createAsyncThunk(
  "titanTab/editCompanyProductRuleData",
  async ({ prescribedItemId, ...body }, thunkAPI) => {
    const url = editCompanyProductRuleUrl(prescribedItemId);
    const respData = await httpPut(url, { ...body, prescribedItemId });
    return respData;
  },
);

export const searchProductDrugData = createAsyncThunk(
  "titanTab/searchProductDrugData",
  async (keyword, thunkAPI) => {
    const urlToGet = searchProductDrugUrl(keyword);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const removeCompanyProductRuleData = createAsyncThunk(
  "titanTab/removeCompanyProductRuleData",
  async ({ prescribedItemId, productRuleId }, thunkAPI) => {
    const urlToGet = removeCompanyProductRuleUrl(
      prescribedItemId,
      productRuleId,
    );
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const getHistoricalConcessionData = createAsyncThunk(
  "titanTab/getHistoricalConcessionData",
  async (_, thunkAPI) => {
    const url = getHistoricalConcessionUrl();
    const respData = await httpGet(url);
    return respData;
  },
);

export const getMonthlyConcessionData = createAsyncThunk(
  "titanTab/getMonthlyConcessionData",
  async (concessionMonth, thunkAPI) => {
    const url = getMonthlyConcessionUrl(concessionMonth);
    const respData = await httpGet(url);
    return respData;
  },
);

export const addConcessionData = createAsyncThunk(
  "titanTab/addConcessionData",
  async (payload, thunkAPI) => {
    const url = addConcessionUrl();
    const respData = await httpPost(url, payload);
    return respData;
  },
);

export const getProductRuleAuditData = createAsyncThunk(
  "titanTab/getProductRuleAuditData",
  async ({ prescribedItemId, productRuleId }, thunkAPI) => {
    const urlToGet = getProductRuleAuditUrl(prescribedItemId, productRuleId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const getTitanClaimsData = createAsyncThunk(
  "titanTab/getTitanClaimsData",
  async (pharmacyId, thunkAPI) => {
    const url = getTitanClaimsUrl(pharmacyId);
    const respData = await withServerMessage(() =>
      httpGet(url, { skipErrorPage: true }),
    );
    return respData;
  },
);

export const getPrescriptionImageData = createAsyncThunk(
  "titanTab/getPrescriptionImageData",
  async ({ pharmacyId, incomingId }, thunkAPI) => {
    const url = getPrescriptionImageUrl(pharmacyId, incomingId);
    const respData = await httpGet(url);
    return respData;
  },
);

export const getItemInfoData = createAsyncThunk(
  "titanTab/getItemInfoData",
  async ({ pharmacyId, prescribedItems }, thunkAPI) => {
    const url = getItemInfoUrl(pharmacyId);
    const respData = await httpPost(url, prescribedItems, { skipErrorPage: true });
    return respData;
  },
);

export const getFp34SummaryData = createAsyncThunk(
  "titanTab/getFp34SummaryData",
  async ({ pharmacyId, cycleMonth }, thunkAPI) => {
    const url = getFp34SummaryUrl(pharmacyId, cycleMonth);
    const respData = await withServerMessage(() =>
      httpGet(url, { skipErrorPage: true }),
    );
    return respData;
  },
);

export const getFp34AdditionalFeesData = createAsyncThunk(
  "titanTab/getFp34AdditionalFeesData",
  async ({ pharmacyId, cycleMonth }, thunkAPI) => {
    const url = getFp34AdditionalFeesUrl(pharmacyId, cycleMonth);
    const respData = await withServerMessage(() =>
      httpGet(url, { skipErrorPage: true }),
    );
    return respData;
  },
);

export const getPharmacyPrivateServicesIncomeData = createAsyncThunk(
  "titanTab/getPharmacyPrivateServicesIncomeData",
  async ({ companyId, pharmacyId, month }, thunkAPI) => {
    const url = getPharmacyPrivateServicesIncomeUrl(companyId, pharmacyId, month);
    const respData = await httpGet(url);
    return respData;
  },
);

export const getDispensingIncomeData = createAsyncThunk(
  "titanTab/getDispensingIncomeData",
  async ({ pharmacyId, fromDate, toDate }, thunkAPI) => {
    const url = getDispensingIncomeUrl(pharmacyId, fromDate, toDate);
    const respData = await withServerMessage(() =>
      httpGet(url, { skipErrorPage: true }),
    );
    return respData;
  },
);

export const getPharmacyPrivateServicesIncomeByDateRangeData = createAsyncThunk(
  "titanTab/getPharmacyPrivateServicesIncomeByDateRangeData",
  async ({ companyId, pharmacyId, startDate, endDate }, thunkAPI) => {
    const url = getPharmacyPrivateServicesIncomeByDateRangeUrl(
      companyId,
      pharmacyId,
      startDate,
      endDate,
    );
    const respData = await httpGet(url);
    return respData;
  },
);

export const saveClaimsData = createAsyncThunk(
  "titanTab/saveClaimsData",
  async ({ pharmacyId, prescriptionIds }, thunkAPI) => {
    const url = saveClaimsUrl(pharmacyId);
    const respData = await httpPost(url, prescriptionIds, { skipErrorPage: true });
    return respData;
  },
);

export const processClaimData = createAsyncThunk(
  "titanTab/processClaimData",
  async ({ pharmacyId, claim }, thunkAPI) => {
    const url = processClaimUrl(pharmacyId);
    const respData = await httpPost(url, claim);
    return respData;
  },
);

export const replacementDispenseNotificationData = createAsyncThunk(
  "titanTab/replacementDispenseNotificationData",
  async ({ pharmacyId, request }, thunkAPI) => {
    const url = replacementDispenseNotificationUrl(pharmacyId);
    const respData = await httpPost(url, request, { skipErrorPage: true });
    return respData;
  },
);

export const bulkRtecData = createAsyncThunk(
  "titanTab/bulkRtecData",
  async ({ pharmacyId, request }, thunkAPI) => {
    const url = bulkRtecUrl(pharmacyId);
    const respData = await httpPost(url, request, { skipErrorPage: true });
    return respData;
  },
);

export const getClaimedCurrentData = createAsyncThunk(
  "titanTab/getClaimedCurrentData",
  async (pharmacyId, thunkAPI) => {
    const url = getClaimedCurrentUrl(pharmacyId);
    const respData = await withServerMessage(() =>
      httpGet(url, { skipErrorPage: true }),
    );
    return respData;
  },
);

export const getClaimedLastData = createAsyncThunk(
  "titanTab/getClaimedLastData",
  async (pharmacyId, thunkAPI) => {
    const url = getClaimedLastUrl(pharmacyId);
    const respData = await withServerMessage(() =>
      httpGet(url, { skipErrorPage: true }),
    );
    return respData;
  },
);

export const claimAmendmentData = createAsyncThunk(
  "titanTab/claimAmendmentData",
  async ({ pharmacyId, request }, thunkAPI) => {
    const url = claimAmendmentUrl(pharmacyId);
    const respData = await httpPost(url, request, { skipErrorPage: true });
    return respData;
  },
);

export const getBlockedClaimsData = createAsyncThunk(
  "titanTab/getBlockedClaimsData",
  async (pharmacyId, thunkAPI) => {
    const url = getBlockedClaimsUrl(pharmacyId);
    const respData = await withServerMessage(() =>
      httpGet(url, { skipErrorPage: true }),
    );
    return respData;
  },
);

export const addEndorsementData = createAsyncThunk(
  "titanTab/addEndorsementData",
  async ({ pharmacyId, request }, thunkAPI) => {
    const url = addEndorsementUrl(pharmacyId);
    const respData = await httpPost(url, request, { skipErrorPage: true });
    return respData;
  },
);
export const addUpdateExemptionData = createAsyncThunk(
  "titanTab/addUpdateExemptionData",
  async ({ pharmacyId, exemption }, thunkAPI) => {
    const url = addUpdateExemptionUrl(pharmacyId);
    const respData = await httpPost(url, exemption, { skipErrorPage: true });
    return respData;
  },
);

export const getExemptionData = createAsyncThunk(
  "titanTab/getExemptionData",
  async ({ pharmacyId, prescriptionId }, thunkAPI) => {
    const url = getExemptionUrl(pharmacyId, prescriptionId);
    const respData = await httpGet(url, { skipErrorPage: true });
    return respData;
  },
);

export const getExpiringItemsData = createAsyncThunk(
  "titanTab/getExpiringItemsData",
  async (pharmacyId, thunkAPI) => {
    const url = getExpiringItemsUrl(pharmacyId);
    const respData = await httpGet(url, { skipErrorPage: true });
    return respData;
  },
);

export const bulkExemptionData = createAsyncThunk(
  "titanTab/bulkExemptionData",
  async ({ pharmacyId, request }, thunkAPI) => {
    const url = bulkExemptionUrl(pharmacyId);
    const respData = await httpPost(url, request, { skipErrorPage: true });
    return respData;
  },
);

export const bulkClearClaimsData = createAsyncThunk(
  "titanTab/bulkClearClaimsData",
  async ({ pharmacyId, prescriptionIds }, thunkAPI) => {
    const url = bulkClearClaimsUrl(pharmacyId);
    const respData = await httpPost(url, { prescriptionIds }, { skipErrorPage: true });
    return respData;
  },
);

export const saveClaimPrescriptionItemsData = createAsyncThunk(
  "titanTab/saveClaimPrescriptionItemsData",
  async ({ pharmacyId, request }, thunkAPI) => {
    const url = saveClaimPrescriptionItemsUrl(pharmacyId);
    const respData = await httpPost(url, request, { skipErrorPage: true });
    return respData;
  },
);

export const getClaimPrescriptionItemsData = createAsyncThunk(
  "titanTab/getClaimPrescriptionItemsData",
  async ({ pharmacyId, prescriptionId }, thunkAPI) => {
    const url = getClaimPrescriptionItemsUrl(pharmacyId, prescriptionId);
    const respData = await httpGet(url, { skipErrorPage: true });
    return respData;
  },
);

export const getConcessionPricesData = createAsyncThunk(
  "titanTab/getConcessionPricesData",
  async ({ pharmacyId, prescribedItems }, thunkAPI) => {
    const url = getConcessionPricesUrl(pharmacyId);
    const respData = await httpPost(url, prescribedItems, { skipErrorPage: true });
    return respData;
  },
);

export const getGroupModeServicesData = createAsyncThunk(
  "titanTab/getGroupModeServicesData",
  async (companyID, thunkAPI) => {
    const url = getGroupModeServicesUrl(companyID);
    const respData = await httpGet(url);
    return respData;
  },
);

export const getDashboardStatsData = createAsyncThunk(
  "titanTab/getDashboardStatsData",
  async (companyId, thunkAPI) => {
    const url = getDashboardStatsUrl(companyId);
    const respData = await httpGet(url);
    return respData;
  },
);

export const titanTabSlice = createSlice({
  name: "titanTab",
  initialState,
  reducers: {
    setIsTitanTabLoading: (state, action) => {
      state.isTitanTabLoading = action?.payload || false;
    },
    setCompanyProductRules: (state, action) => {
      state.companyProductRules = action?.payload || [];
    },
    resetTitanTabState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCompanyProductRulesByCompanyIdData.pending, (state) => {
        state.isTitanTabLoading = true;
      })
      .addCase(
        getCompanyProductRulesByCompanyIdData.fulfilled,
        (state, action) => {
          state.isTitanTabLoading = false;
          state.companyProductRules = action?.payload || [];
        },
      )
      .addCase(
        getCompanyProductRulesByCompanyIdData.rejected,
        (state, action) => {
          logErrorWithPayload(action?.error);
          state.isTitanTabLoading = false;
          state.companyProductRules = [];
        },
      )
      .addCase(editCompanyProductRuleData.pending, (state) => {
        state.isTitanTabLoading = true;
      })
      .addCase(editCompanyProductRuleData.fulfilled, (state) => {
        state.isTitanTabLoading = false;
      })
      .addCase(editCompanyProductRuleData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isTitanTabLoading = false;
      })
      .addCase(addCompanyProductRuleData.pending, (state) => {
        state.isTitanTabLoading = true;
      })
      .addCase(addCompanyProductRuleData.fulfilled, (state) => {
        state.isTitanTabLoading = false;
      })
      .addCase(addCompanyProductRuleData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isTitanTabLoading = false;
      })
      .addCase(searchProductDrugData.pending, (state) => {
        state.isDrugSearchLoading = true;
      })
      .addCase(searchProductDrugData.fulfilled, (state, action) => {
        state.isDrugSearchLoading = false;
        state.drugSearchResults = action?.payload?.response || [];
      })
      .addCase(searchProductDrugData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isDrugSearchLoading = false;
        state.drugSearchResults = [];
      })
      .addCase(removeCompanyProductRuleData.pending, (state) => {
        state.isRemovingProductRule = true;
      })
      .addCase(removeCompanyProductRuleData.fulfilled, (state) => {
        state.isRemovingProductRule = false;
      })
      .addCase(removeCompanyProductRuleData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isRemovingProductRule = false;
      })
      .addCase(getHistoricalConcessionData.pending, (state) => {
        state.isHistoricalConcessionLoading = true;
        state.historicalConcessions = [];
      })
      .addCase(getHistoricalConcessionData.fulfilled, (state, action) => {
        state.isHistoricalConcessionLoading = false;
        state.historicalConcessions = action?.payload || [];
      })
      .addCase(getHistoricalConcessionData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isHistoricalConcessionLoading = false;
        state.historicalConcessions = [];
      })
      .addCase(getMonthlyConcessionData.pending, (state) => {
        state.isMonthlyConcessionLoading = true;
        state.monthlyConcessions = [];
      })
      .addCase(getMonthlyConcessionData.fulfilled, (state, action) => {
        state.isMonthlyConcessionLoading = false;
        state.monthlyConcessions = action?.payload || [];
      })
      .addCase(getMonthlyConcessionData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isMonthlyConcessionLoading = false;
        state.monthlyConcessions = [];
      })
      .addCase(addConcessionData.pending, (state) => {
        state.isAddingConcession = true;
      })
      .addCase(addConcessionData.fulfilled, (state) => {
        state.isAddingConcession = false;
      })
      .addCase(addConcessionData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isAddingConcession = false;
      })
      .addCase(getProductRuleAuditData.pending, (state) => {
        state.isAuditLoading = true;
        state.productRuleAuditEntries = [];
      })
      .addCase(getProductRuleAuditData.fulfilled, (state, action) => {
        state.isAuditLoading = false;
        state.productRuleAuditEntries = action?.payload?.auditEntries || [];
      })
      .addCase(getProductRuleAuditData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isAuditLoading = false;
        state.productRuleAuditEntries = [];
      })
      .addCase(getTitanClaimsData.pending, (state) => {
        state.isTitanClaimsLoading = true;
        state.titanClaims = [];
      })
      .addCase(getTitanClaimsData.fulfilled, (state, action) => {
        state.isTitanClaimsLoading = false;
        state.titanClaims = action?.payload?.data || [];
      })
      .addCase(getTitanClaimsData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isTitanClaimsLoading = false;
        state.titanClaims = [];
      })
      .addCase(getClaimedCurrentData.pending, (state) => {
        state.isClaimedCurrentClaimsLoading = true;
        state.claimedCurrentClaims = [];
      })
      .addCase(getClaimedCurrentData.fulfilled, (state, action) => {
        state.isClaimedCurrentClaimsLoading = false;
        state.claimedCurrentClaims = action?.payload?.data || [];
      })
      .addCase(getClaimedCurrentData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isClaimedCurrentClaimsLoading = false;
        state.claimedCurrentClaims = [];
      })
      .addCase(getClaimedLastData.pending, (state) => {
        state.isClaimedLastClaimsLoading = true;
        state.claimedLastClaims = [];
      })
      .addCase(getClaimedLastData.fulfilled, (state, action) => {
        state.isClaimedLastClaimsLoading = false;
        state.claimedLastClaims = action?.payload?.data || [];
      })
      .addCase(getClaimedLastData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isClaimedLastClaimsLoading = false;
        state.claimedLastClaims = [];
      })
      .addCase(getBlockedClaimsData.pending, (state) => {
        state.isBlockedClaimsLoading = true;
        state.blockedClaims = [];
      })
      .addCase(getBlockedClaimsData.fulfilled, (state, action) => {
        state.isBlockedClaimsLoading = false;
        const payload = action?.payload;
        // get-blocked-claims returns Result<IEnumerable<BlockedClaimSummaryModel>>,
        // so the array is at payload.data (kept .response as a legacy fallback).
        state.blockedClaims = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data
            : payload?.response || [];
      })
      .addCase(getBlockedClaimsData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isBlockedClaimsLoading = false;
        state.blockedClaims = [];
      })
      .addCase(getDashboardStatsData.pending, (state) => {
        state.isDashboardStatsLoading = true;
      })
      .addCase(getDashboardStatsData.fulfilled, (state, action) => {
        state.isDashboardStatsLoading = false;
        const payload = action?.payload;
        state.dashboardStats = Array.isArray(payload) ? payload : payload?.response ?? [];
      })
      .addCase(getDashboardStatsData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isDashboardStatsLoading = false;
        state.dashboardStats = [];
      })

      .addCase(getPharmacyPrivateServicesIncomeByDateRangeData.pending, (state) => {
        state.privateServicesIncomeRangePending += 1;
        state.isPrivateServicesIncomeRangeLoading = true;
      })
      .addCase(getPharmacyPrivateServicesIncomeByDateRangeData.fulfilled, (state, action) => {
        state.privateServicesIncomeRangePending = Math.max(
          0,
          state.privateServicesIncomeRangePending - 1,
        );
        state.isPrivateServicesIncomeRangeLoading =
          state.privateServicesIncomeRangePending > 0;
        const payload = action?.payload;
        const data = payload?.response ?? payload?.data ?? payload;
        const pharmacyId = data?.pharmacyId ?? action?.meta?.arg?.pharmacyId;
        if (pharmacyId) {
          state.privateServicesIncomeRange[pharmacyId] = data ?? null;
        }
      })
      .addCase(getPharmacyPrivateServicesIncomeByDateRangeData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.privateServicesIncomeRangePending = Math.max(
          0,
          state.privateServicesIncomeRangePending - 1,
        );
        state.isPrivateServicesIncomeRangeLoading =
          state.privateServicesIncomeRangePending > 0;
        const pharmacyId = action?.meta?.arg?.pharmacyId;
        if (pharmacyId) {
          state.privateServicesIncomeRange[pharmacyId] = null;
        }
      })

      .addCase(getDispensingIncomeData.pending, (state) => {
        state.dispensingIncomeRangePending += 1;
        state.isDispensingIncomeRangeLoading = true;
      })
      .addCase(getDispensingIncomeData.fulfilled, (state, action) => {
        state.dispensingIncomeRangePending = Math.max(
          0,
          state.dispensingIncomeRangePending - 1,
        );
        state.isDispensingIncomeRangeLoading =
          state.dispensingIncomeRangePending > 0;
        const payload = action?.payload;
        const data = payload?.response ?? payload?.data ?? payload;
        const pharmacyId = action?.meta?.arg?.pharmacyId;
        if (pharmacyId) {
          state.dispensingIncomeRange[pharmacyId] = Array.isArray(data) ? data : [];
        }
      })
      .addCase(getDispensingIncomeData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.dispensingIncomeRangePending = Math.max(
          0,
          state.dispensingIncomeRangePending - 1,
        );
        state.isDispensingIncomeRangeLoading =
          state.dispensingIncomeRangePending > 0;
        const pharmacyId = action?.meta?.arg?.pharmacyId;
        if (pharmacyId) {
          state.dispensingIncomeRange[pharmacyId] = [];
        }
      })

       .addCase(getGroupModeServicesData.pending, (state) => {
        state.isServicesLoading = true;
        state.groupModeServices = [];
      })
      .addCase(getGroupModeServicesData.fulfilled, (state, action) => {
        state.isServicesLoading = false;
        state.groupModeServices = action?.payload;
      })
      .addCase(getGroupModeServicesData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isServicesLoading = false;
        state.groupModeServices = [];
      })
      ;
  },
});

export const {
  setIsTitanTabLoading,
  setCompanyProductRules,
  resetTitanTabState,
} = titanTabSlice.actions;

export default titanTabSlice.reducer;
